/**
 * A run in words, never in characters (production visual audit, owner decision 2026-10-10, A1).
 *
 * A planner does not read «780c69ce…». Banners, headings, buttons and provenance lines named a run
 * by its id, in three lengths (6, 8 and 32 characters). This is the ONE place that turns a run into
 * a name: «CO · 2026-W41 · Negozi diretti · eseguita il 9 ott 2026», built from the run directory
 * the app already reads for its pickers (`GET /inseason/runs`, a warm cache entry on every
 * run-scoped page).
 *
 * What is said, in order of what is known:
 *
 *   1. the run is in the directory — market, period, channel and the day it ran;
 *   2. the caller knows only a day — «esecuzione del 9 ott 2026»;
 *   3. nothing is known — «un’esecuzione precedente». Never the id, never a guess.
 *
 * **The id is not deleted, it is moved.** Support needs it, so it lives in {@link IdTag}: a small
 * «id» whose tooltip carries the whole id and whose press copies it. Where an id must still be
 * shown as a short code (a column that a support person sorts by), {@link shortRunId} is the one
 * width, eight characters.
 */

import { useContext, useMemo, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { QueryClientContext } from '@tanstack/react-query';

import { useApiCollection } from '@/api';
import { inseasonListRuns } from '@/api/generated';
import type { CycleRunSummary } from '@/api/generated';
import { formatDate, formatDateTime } from '@/lib/format';
import { AboutValuesContext, FOCUS_RING, Tooltip } from '@/primitives';

import { codeWords, ENTRY_POINT_LABEL_KEY } from './words';

/** A run's short id — data, not copy. The one width wherever an id remains on screen (A1). */
export const SHORT_ID_LENGTH = 8;

/** The first {@link SHORT_ID_LENGTH} characters of `id`. */
export function shortRunId(id: string): string {
  return id.slice(0, SHORT_ID_LENGTH);
}

/** The part of a directory row that names a run. */
export type RunNamingRow = Pick<
  CycleRunSummary,
  'run_id' | 'country_key' | 'period_key' | 'entry_point_id' | 'cycle_tick' | 'recorded_at' | 'generated_at'
>;

/** The instant a run ran: its `cycle_tick`, else its `recorded_at`, else `generated_at`. */
function ranAt(row: Pick<CycleRunSummary, 'cycle_tick' | 'recorded_at' | 'generated_at'>): string {
  return row.cycle_tick ?? row.recorded_at ?? row.generated_at;
}

/**
 * When a run ran, for a picker's label: its `cycle_tick`, else its `recorded_at`, as a date AND a
 * time in UTC (`docs/adr/9034`).
 *
 * The label used `generated_at`, which holds the PERIOD END: every run of a month read «generated
 * 01/10/2026», so the list could not tell one tick from the next. The tick is when the cycle ran;
 * `recorded_at` (ADR 6034) is when the run was stored, and stands in where a run has no tick. The
 * time is shown because a market ticks more than once a day, and in UTC because the platform's
 * schedule is UTC and a local time would move with the planner's machine. Unreadable: verbatim.
 * The identifier stays `run_id`; nothing parses the label back. Pure.
 *
 * (Moved here from `features/inseason/CycleRunPicker.tsx`, which re-exports it.)
 */
export function runMoment(row: Pick<CycleRunSummary, 'cycle_tick' | 'recorded_at' | 'generated_at'>, language: string): string {
  const at = ranAt(row);
  return Number.isNaN(new Date(at).getTime()) ? at : formatDateTime(at, language, 'UTC');
}

/** The day `iso` falls on, UTC (the platform's schedule is UTC); `null` when unreadable. */
function dayOf(iso: string | null | undefined, language: string): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  return Number.isNaN(new Date(iso).getTime()) ? null : formatDate(iso, language, 'UTC');
}

type Say = TFunction;

/**
 * A run directory row in words: «CO · 2026-W41 · Negozi diretti · eseguita il 9 ott 2026». A day
 * that cannot be read is left out of the sentence rather than printed raw. Pure.
 */
export function runWords(row: RunNamingRow, t: Say, language: string): string {
  const day = dayOf(ranAt(row), language);
  const values = { country: row.country_key, period: row.period_key, entryPoint: channelOf(row.entry_point_id, t) };
  if (values.entryPoint === '') {
    return day === null ? t('runWords.undatedNoChannel', values) : t('runWords.labelNoChannel', { ...values, date: day });
  }
  return day === null ? t('runWords.undated', values) : t('runWords.label', { ...values, date: day });
}

/** The channel a run is for, in words; empty when the row names none (a platform that does not publish it). */
function channelOf(entryPoint: string | null | undefined, t: Say): string {
  return entryPoint === null || entryPoint === undefined || entryPoint === '' ? '' : codeWords(t, ENTRY_POINT_LABEL_KEY, entryPoint);
}

/**
 * A run's name without the day: «CO · 2026-W41 · Negozi diretti» — for a list that shows the day
 * (and the time) in a column of its own. Pure.
 */
export function runName(row: Pick<RunNamingRow, 'country_key' | 'period_key' | 'entry_point_id'>, t: Say): string {
  const values = { country: row.country_key, period: row.period_key, entryPoint: channelOf(row.entry_point_id, t) };
  return values.entryPoint === '' ? t('runWords.undatedNoChannel', values) : t('runWords.undated', values);
}

/**
 * A run as a picker's option: its name and the minute it ran — «CO · 2026-W41 · Negozi diretti ·
 * eseguita il 11 ott 2026, 11:13 UTC» — with a stale stock position said (audit 2, B). A list of
 * runs differs by WHEN they ran (`generated_at` is the period end and tells them apart by
 * nothing), so the option carries the tick's time; the identifier is never a distinguisher — it
 * sits behind the «id» beside the picker. Pure.
 */
export function runPickerWords(row: RunNamingRow & Partial<Pick<CycleRunSummary, 'stock_partition_is_fresh'>>, t: Say, language: string): string {
  const values = { name: runName(row, t), moment: runMoment(row, language) };
  return row.stock_partition_is_fresh === false ? t('runWords.pickerOptionStale', values) : t('runWords.pickerOption', values);
}

/** What is said of a run the directory does not list: its day when known, else nothing certain. Pure. */
export function runWordsFallback(t: Say, language: string, ranOn?: string | null): string {
  const day = dayOf(ranOn, language);
  return day === null ? t('runWords.unknown') : t('runWords.dated', { date: day });
}

/**
 * The fallback for a run named right AFTER the noun — «l’esecuzione {run}», «dell’esecuzione
 * {run}» — where «un’esecuzione precedente» would say the noun twice: «del 9 ott 2026» when the day
 * is known, «indicata» when nothing is. Pure.
 */
export function runSlotFallback(t: Say, language: string, ranOn?: string | null): string {
  const day = dayOf(ranOn, language);
  return day === null ? t('runWords.slotUnknown') : t('runWords.slotDated', { date: day });
}

/** A run as a directory lookup: `undefined` while the directory is read, `[]` if it failed. */
export function findRun(directory: readonly RunNamingRow[] | undefined, runId: string): RunNamingRow | undefined {
  return directory?.find((row) => row.run_id === runId);
}

/** How a caller names a run: its id, and — when the caller has it — the day it ran. */
export type RunWordsOf = (runId: string | null | undefined, ranOn?: string | null) => string;

/**
 * The run directory the pickers have already read (one request, one cache entry), as the three
 * ways a run is named: `words` standing alone («CO · 2026-W41 · Negozi diretti · eseguita il 9 ott
 * 2026»; «esecuzione del 9 ott 2026»; «un’esecuzione precedente»), `slot` straight after the noun
 * «esecuzione» (the same words; «del 9 ott 2026»; «indicata»), and `known` (the words, or `null`
 * when the directory does not list the run — for a sentence that must not guess).
 */
export interface RunNamer {
  readonly words: RunWordsOf;
  readonly slot: RunWordsOf;
  readonly known: (runId: string | null | undefined) => string | null;
}

export function useRunNamer(enabled = true): RunNamer {
  const { t, i18n } = useTranslation();
  const directory = useApiCollection(inseasonListRuns, { query: {} }, { enabled });
  const rows = directory.data;
  const language = i18n.language;
  // Stable between renders until the directory or the language changes: a callback may depend on it.
  return useMemo((): RunNamer => {
    const known = (runId: string | null | undefined): string | null => {
      if (runId === null || runId === undefined || runId === '') return null;
      const row = findRun(rows, runId);
      return row === undefined ? null : runWords(row, t, language);
    };
    return {
      words: (runId, ranOn) => known(runId) ?? runWordsFallback(t, language, ranOn),
      slot: (runId, ranOn) => known(runId) ?? runSlotFallback(t, language, ranOn),
      known,
    };
  }, [rows, t, language]);
}

/** {@link RunNamer.words} as a function — `useRunWordsOf()(runId)`. */
export function useRunWordsOf(enabled = true): RunWordsOf {
  return useRunNamer(enabled).words;
}

/** One run in words, standing alone — a heading, a button, a table cell. */
export function useRunWords(runId: string | null | undefined, ranOn?: string | null): string {
  return useRunNamer().words(runId, ranOn);
}

/** One run in words, right after the noun «esecuzione» ({@link RunNamer.slot}). */
export function useRunSlot(runId: string | null | undefined, ranOn?: string | null): string {
  return useRunNamer().slot(runId, ranOn);
}

/**
 * A saved thing the platform names only by an identifier — a simulation, a campaign — named by the
 * moment it was asked for: «Simulazione del 9 ott 2026, 11:13» (audit A1). `what` picks the noun;
 * with no readable moment the noun stands alone. The id belongs in an {@link IdTag}. Pure.
 */
export function datedName(what: 'simulation' | 'campaignSimulation' | 'change', at: string | null | undefined, t: Say, language: string): string {
  // A calendar date as a date; an instant as a date and a time in UTC, said so.
  const moment = at === null || at === undefined || Number.isNaN(new Date(at).getTime()) ? null
    : /^\d{4}-\d{2}-\d{2}$/.test(at) ? formatDate(at, language, 'UTC') : t('idWords.instantUtc', { instant: formatDateTime(at, language, 'UTC') });
  switch (what) {
    case 'simulation': return moment === null ? t('idWords.simulationUndated') : t('idWords.simulation', { date: moment });
    case 'campaignSimulation': return moment === null ? t('idWords.campaignSimulationUndated') : t('idWords.campaignSimulation', { date: moment });
    case 'change': return moment === null ? t('idWords.changeUndated') : t('idWords.change', { date: moment });
  }
}

/**
 * Whether a query client is mounted: a template part drawn in a unit test or a fixture page may
 * have none, and then the run directory is not read and the words are the fallback.
 */
export function useHasQueryClient(): boolean {
  return useContext(QueryClientContext) !== undefined;
}

/**
 * The small «id» for the person who supports the platform: a tooltip with the whole identifier
 * and a press that copies it. Everything a planner reads is in words; this is the way back to
 * the identifier. Keyboard reachable (a button), and its name says what it copies.
 */
export function IdTag({ id, kind }: {
  readonly id: string;
  /** What the id is — «esecuzione», «simulazione» — for the accessible name; defaults to «identificativo». */
  readonly kind?: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  if (id === '') return null;
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied('yes');
    } catch {
      setCopied('failed');
    }
  };
  const what = kind ?? t('idTag.kindDefault');
  return (
    <Tooltip content={copied === 'yes' ? t('idTag.copied', { id }) : copied === 'failed' ? t('idTag.copyFailed', { id }) : id}>
      <button
        type="button"
        data-id-tag={id}
        aria-label={t('idTag.copyAria', { kind: what, id })}
        onClick={() => { void copy(); }}
        onBlur={() => setCopied(null)}
        className={`border-hairline text-label text-ink-secondary font-numeric rounded-chip border px-1 align-baseline ${FOCUS_RING}`}
      >
        {t('idTag.label')}
      </button>
    </Tooltip>
  );
}

/**
 * A run named in words with its «id» beside it — the standing way to show a run in running
 * text, a heading or a table cell.
 */
export function RunWords({ runId, ranOn, words: given }: {
  readonly runId: string;
  readonly ranOn?: string | null;
  /** The run already named — a run of another kind (a consolidation) the in-season directory does not list. */
  readonly words?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  // A run already named does not read the in-season directory at all.
  const named = useRunNamer(given === undefined).words(runId, ranOn);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-1">
      <span>{given ?? named}</span>
      <IdTag id={runId} kind={t('idTag.kindRun')} />
    </span>
  );
}

// ── the (i) texts ────────────────────────────────────────────────────────────

/** An identifier as the platform mints them: hexadecimal, possibly dashed — not a name or a label. */
const IDENTIFIER = /^[0-9a-f]{6,}(?:-[0-9a-f]+)*$/i;

/** The placeholders an (i) text uses for a run. */
const RUN_VALUE_KEYS = ['run', 'runId'] as const;

/**
 * The (i) texts' run, in words: the hook `RunWordsAboutProvider` hands every popover. The run
 * directory is read only when a value is a run identifier, and only while a popover is open.
 */
function useRunWordsAboutValues(values: Readonly<Record<string, string | number>>): Readonly<Record<string, string | number>> {
  const { t, i18n } = useTranslation();
  const runs = RUN_VALUE_KEYS.filter((key) => {
    const value = values[key];
    return typeof value === 'string' && IDENTIFIER.test(value);
  });
  const directory = useApiCollection(inseasonListRuns, { query: {} }, { enabled: runs.length > 0 });
  const rows = directory.data;
  const language = i18n.language;
  return useMemo(() => {
    if (runs.length === 0) return values;
    const named: Record<string, string | number> = { ...values };
    for (const key of runs) {
      const row = findRun(rows, String(values[key]));
      named[key] = row === undefined ? runSlotFallback(t, language) : runWords(row, t, language);
    }
    return named;
    // `runs` is derived from `values`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, rows, t, language]);
}

/**
 * Mount once, inside the query client: every (i) text that names a run (`{run}` in the `help`
 * catalogue — 170 of them) says it in words, from the run directory the pages already read. A run
 * the directory does not list is «del 9 ott 2026» / «indicata», never the id; a value that is not
 * an identifier (a dash, a label) is left as the page gave it.
 */
export function RunWordsAboutProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <AboutValuesContext.Provider value={useRunWordsAboutValues}>{children}</AboutValuesContext.Provider>;
}
